/**
 * Product data-issue reporting — client constants and context builders.
 * Keep category/status values aligned with express_app/constants/productIssue.js.
 */

export const PRODUCT_ISSUE_STATUS = {
  REPORTED: 'reported',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
}

export const PRODUCT_ISSUE_STATUS_LABELS = {
  reported: 'Reported',
  in_progress: 'In progress',
  resolved: 'Resolved',
}

export const PRODUCT_ISSUE_SCOPE = {
  PRODUCT: 'product',
  PRICE_FILE: 'price_file',
}

export const PRODUCT_ISSUE_SCOPE_LABELS = {
  product: 'Product',
  price_file: 'Price File',
}

export const PRODUCT_ISSUE_CATEGORIES = [
  {
    value: 'incorrect_vat',
    label: 'Incorrect VAT',
  },
  {
    value: 'incorrect_price',
    label: 'Incorrect price',
  },
  {
    value: 'incorrect_pack_size',
    label: 'Incorrect pack size or unit',
  },
  {
    value: 'incorrect_product_information',
    label: 'Incorrect product information',
  },
  {
    value: 'other',
    label: 'Other issue',
  },
]

export const PRODUCT_ISSUE_CATEGORY_LABELS = PRODUCT_ISSUE_CATEGORIES.reduce(
  (acc, item) => {
    acc[item.value] = item.label
    return acc
  },
  {},
)

/** Whole price-file / catalogue report categories (ticket wording). */
export const PRICE_FILE_ISSUE_CATEGORIES = [
  {
    value: 'incorrect_or_outdated_prices',
    label: 'Prices are incorrect or outdated',
  },
  {
    value: 'incorrect_vat',
    label: 'VAT information is incorrect',
  },
  {
    value: 'incorrect_or_outdated_product_codes',
    label: 'Product codes are incorrect or outdated',
  },
  {
    value: 'missing_products',
    label: 'Products are missing',
  },
  {
    value: 'duplicate_or_discontinued_products',
    label: 'Duplicate or discontinued products',
  },
  {
    value: 'wrong_catalogue_or_pricing',
    label: 'Wrong catalogue or customer-specific pricing',
  },
  {
    value: 'other',
    label: 'Other / multiple issues',
  },
]

export const PRICE_FILE_ISSUE_CATEGORY_LABELS =
  PRICE_FILE_ISSUE_CATEGORIES.reduce((acc, item) => {
    acc[item.value] = item.label
    return acc
  }, {})

export const PRODUCT_ISSUE_SOURCE = {
  VENUE_PRODUCTS: 'venue_products',
  ORDERS: 'orders',
  ORDERED_PRODUCTS: 'ordered_products',
  INVOICES: 'invoices',
  CATALOGUE: 'catalogue',
  ADMIN_PRODUCTS: 'admin_products',
  RECEIPTING: 'receipting',
  YOUR_SUPPLIERS: 'your_suppliers',
  OTHER: 'other',
}

export const PRODUCT_ISSUE_EXPLANATION_MAX_LENGTH = 2000

export const PRICE_FILE_UNSURE_VALUE = '__unsure__'

function formatPrice(value) {
  if (value == null || value === '') return ''
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value)
  return `£${num.toFixed(2)}`
}

function vatLabel({ vatTreatment, hasVat } = {}) {
  if (vatTreatment === 'standard') return 'Standard-rated'
  if (vatTreatment === 'zeroRated') return 'Zero-rated'
  if (vatTreatment === 'noVat') return 'No VAT'
  if (vatTreatment) return String(vatTreatment)
  if (hasVat === true) return 'Standard-rated'
  if (hasVat === false) return 'No VAT / zero-rated'
  return ''
}

/**
 * Normalise product/order/invoice row shapes into the payload the API expects.
 */
export function buildProductIssueContext({
  product,
  order,
  invoice,
  supplier,
  supplierTitle,
  sourceScreen = PRODUCT_ISSUE_SOURCE.OTHER,
} = {}) {
  const productDoc = product?.product || product || order?.product || {}
  const orderDoc = order || {}
  const supplierDoc =
    supplier ||
    productDoc.supplier ||
    orderDoc.supplier ||
    product?.supplier ||
    {}

  const title = productDoc.title || orderDoc.title || product?.title || ''
  const sku = productDoc.sku || orderDoc.sku || ''
  const suppliersProductCode =
    productDoc.suppliersProductCode || orderDoc.suppliersProductCode || ''
  const size = productDoc.size || orderDoc.size || ''
  const uom = productDoc.UoM || orderDoc.UoM || ''
  const pricedBy = productDoc.pricedBy || orderDoc.pricedBy || ''
  const priceRaw =
    orderDoc.price ?? productDoc.price ?? product?.price ?? productDoc.OMSPrice
  const hasVat =
    orderDoc.hasVat !== undefined
      ? orderDoc.hasVat
      : productDoc.hasVat !== undefined
      ? productDoc.hasVat
      : undefined
  const vatTreatment =
    orderDoc.vatTreatment || productDoc.vatTreatment || undefined

  const productId =
    productDoc._id ||
    productDoc.id ||
    orderDoc.product?._id ||
    orderDoc.product ||
    product?._id ||
    product?.id ||
    null

  const supplierId =
    supplierDoc._id ||
    supplierDoc.id ||
    (typeof productDoc.supplier === 'string' ? productDoc.supplier : null) ||
    (typeof orderDoc.supplier === 'string' ? orderDoc.supplier : null) ||
    null

  return {
    productId: productId ? String(productId) : undefined,
    supplierId: supplierId ? String(supplierId) : undefined,
    supplierTitle:
      supplierTitle ||
      supplierDoc.title ||
      product?.supplierTitle ||
      orderDoc.supplierTitle ||
      '',
    orderId: orderDoc._id || orderDoc.id || undefined,
    invoiceId: invoice?._id || invoice?.id || undefined,
    sourceScreen,
    productSnapshot: {
      description: title,
      productCode: suppliersProductCode || sku,
      price: formatPrice(priceRaw),
      packSize: size,
      vatRate: vatLabel({ vatTreatment, hasVat }),
      sellingUnit: uom || pricedBy,
      sku,
      suppliersProductCode,
    },
  }
}

export function getProductIssueCategoryLabel(category, scope) {
  if (scope === PRODUCT_ISSUE_SCOPE.PRICE_FILE) {
    return (
      PRICE_FILE_ISSUE_CATEGORY_LABELS[category] ||
      PRODUCT_ISSUE_CATEGORY_LABELS[category] ||
      category ||
      ''
    )
  }
  return (
    PRODUCT_ISSUE_CATEGORY_LABELS[category] ||
    PRICE_FILE_ISSUE_CATEGORY_LABELS[category] ||
    category ||
    ''
  )
}

export function getProductIssueScopeLabel(scope) {
  return PRODUCT_ISSUE_SCOPE_LABELS[scope] || scope || ''
}

export function getProductIssueStatusLabel(status) {
  return PRODUCT_ISSUE_STATUS_LABELS[status] || status || ''
}

/** Legacy rows without scope are treated as product-level. */
export function resolveProductIssueScope(issue) {
  if (issue?.scope === PRODUCT_ISSUE_SCOPE.PRICE_FILE) {
    return PRODUCT_ISSUE_SCOPE.PRICE_FILE
  }
  return PRODUCT_ISSUE_SCOPE.PRODUCT
}

/** Primary label for the Product / catalogue column. */
export function getProductIssueSubjectLabel(issue) {
  if (resolveProductIssueScope(issue) === PRODUCT_ISSUE_SCOPE.PRICE_FILE) {
    if (issue?.priceFileUnsure) return 'Entire price file'
    return issue?.priceFileTitle || 'Entire price file'
  }
  return issue?.productSnapshot?.description || '—'
}

/** Secondary muted line under the subject. */
export function getProductIssueSubjectDetail(issue) {
  if (resolveProductIssueScope(issue) === PRODUCT_ISSUE_SCOPE.PRICE_FILE) {
    return issue?.priceFileUnsure ? 'Catalogue not specified' : ''
  }
  return issue?.productSnapshot?.productCode || ''
}

export function getProductIssueRoutingLabel(issue) {
  const routing = issue?.routing || {}
  if (routing.sent) {
    return { label: 'Emailed', color: 'green', title: '' }
  }
  if (
    typeof routing.error === 'string' &&
    /already notified/i.test(routing.error)
  ) {
    return {
      label: 'Deferred',
      color: 'blue',
      title: routing.error,
    }
  }
  if (routing.attempted) {
    return {
      label: 'No email',
      color: 'default',
      title: routing.error || '',
    }
  }
  return { label: '—', color: 'default', title: '' }
}

/** Query params for Product Issues list APIs. */
export function buildProductIssuesListQuery({
  view = 'open',
  issueType = 'all',
} = {}) {
  const query = {}
  if (view && view !== 'all') query.view = view
  if (
    issueType === PRODUCT_ISSUE_SCOPE.PRODUCT ||
    issueType === PRODUCT_ISSUE_SCOPE.PRICE_FILE
  ) {
    query.issueType = issueType
  }
  return query
}

export function getProductIssuesEmptyText({
  view = 'open',
  issueType = 'all',
} = {}) {
  const resolved = view === 'resolved'
  if (issueType === PRODUCT_ISSUE_SCOPE.PRICE_FILE) {
    return resolved
      ? 'No resolved price file issues yet.'
      : 'No open price file issues.'
  }
  if (issueType === PRODUCT_ISSUE_SCOPE.PRODUCT) {
    return resolved
      ? 'No resolved product issues yet.'
      : 'No open product issues.'
  }
  return resolved ? 'No resolved issues yet.' : 'No open issues.'
}

/**
 * Success copy after create. Keeps product-issue wording stable and clarifies
 * catalogue deferred / no-email cases for price-file reports.
 */
export function getProductIssueSubmitFeedback({
  routing = {},
  scope = PRODUCT_ISSUE_SCOPE.PRODUCT,
} = {}) {
  const isPriceFile = scope === PRODUCT_ISSUE_SCOPE.PRICE_FILE
  if (routing?.sent) {
    return isPriceFile
      ? 'Price file issue reported and sent to the supplier'
      : 'Issue reported and sent to the supplier'
  }

  const error = String(routing?.error || '')
  if (/already notified/i.test(error)) {
    return isPriceFile
      ? 'Price file issue reported. Supplier was already notified about an open catalogue issue — tracked in Product Issues.'
      : 'Issue reported. Supplier was already notified about an open issue — tracked in Product Issues.'
  }

  if (error) {
    return isPriceFile
      ? 'Price file issue reported. It is saved in Product Issues for follow-up.'
      : 'Issue reported. No supplier email on file — your team can chase from Product Issues.'
  }

  return isPriceFile ? 'Price file issue reported' : 'Issue reported'
}

export function formatPriceFileOptionLabel(priceFile) {
  if (!priceFile) return ''
  const title = String(priceFile.title || '').trim()
  const secondTitle = String(priceFile.secondTitle || '').trim()
  if (title && secondTitle) return `${title} — ${secondTitle}`
  return secondTitle || title || String(priceFile._id || priceFile.id || '')
}

/**
 * Build create payload for a whole price-file / catalogue issue.
 */
export function buildPriceFileIssuePayload({
  supplierId,
  supplierTitle = '',
  priceFileId,
  priceFileUnsure = false,
  category,
  explanation = '',
} = {}) {
  const unsure = priceFileUnsure === true
  return {
    scope: PRODUCT_ISSUE_SCOPE.PRICE_FILE,
    supplierId: supplierId ? String(supplierId) : undefined,
    supplierTitle: supplierTitle || '',
    priceFileId: unsure || !priceFileId ? undefined : String(priceFileId),
    priceFileUnsure: unsure,
    category,
    explanation: String(explanation || '').trim(),
    sourceScreen: PRODUCT_ISSUE_SOURCE.YOUR_SUPPLIERS,
  }
}
